import { Image } from "@components/Image/Image";
import { Link } from "@components/Link/Link";
import { StyledButton } from "@components/Styled/Button";
import { BackgroundContainer } from "@components/Styled/Container";
import { Video } from "@components/Video/Video";
import { type ThemeAspectRatio } from "@ts/theme";
import React from "react";
import tw, { styled } from "twin.macro";
import { withHeroDuo, type HeroDuoProps } from "./withHeroDuo";

const Title = tw.div`
  font-display text-center leading-h1
`;

const DesktopTitle = tw(Title)`
  text-[32px] text-white lg:text-[44px] xl:text-[56px]
`;

const Wrapper = tw.div`
  w-full relative flex flex-row
`;

const LeftSection = tw.div`
  w-1/2 relative
`;

const RightSection = tw.div`
  w-1/2 relative
`;

const FullSection = tw.div`
  w-full relative grid
`;

const Container = tw.div`
  pointer-events-none w-full h-full absolute flex flex-col items-center justify-center z-20 p-mini md:p-2
`;

const ImageWrapper = tw.div`
  w-full font-display
`;

const ButtonWrapper = tw.div`
  flex w-full justify-center font-display
`;

const Button = styled(StyledButton)`
  ${tw`w-full mt-midi pointer-events-auto text-body p-mini first:mr-mini last:ml-mini md:first-of-type:mr-midi md:last:ml-midi md:max-w-[208px] md:min-w-[180px] md:text-h4`}
`;

const Desktop = tw.div`hidden md:block`;

const Mobile = tw.div`block md:hidden`;

const CTALeftLink = tw(Link)`
  w-1/2 h-full absolute z-10
`;

const CTARightLink = tw(Link)`
  w-1/2 h-full absolute right-0 z-10
`;

const HeroDuo = withHeroDuo(
  ({
    background,
    ctaLeft,
    ctaLeftLink,
    ctaRight,
    ctaRightLink,
    desktopAspectRatio,
    handleClick,
    imageDesktop,
    imageLeft,
    imageLeftDesktop,
    imageMobile,
    imageRight,
    imageRightDesktop,
    mediaType,
    mobileAspectRatio,
    title,
    videoDesktop,
    videoDesktopBackground,
    videoMobile,
    videoMobileBackground,
  }: HeroDuoProps) => {
    return (
      <BackgroundContainer
        width="full"
        background={background}
        data-component-type="section-hero-duo"
      >
        {(() => {
          switch (mediaType) {
            case "Video":
              return (
                <Wrapper>
                  <FullSection>
                    <Container>
                      <DesktopTitle>{title}</DesktopTitle>
                      <ButtonWrapper>
                        <Button
                          as={Link}
                          to={ctaLeftLink?.url}
                          {...ctaLeft}
                          align="right"
                          handleClick={handleClick}
                        >
                          {ctaLeft?.title}
                        </Button>
                        <Button
                          as={Link}
                          to={ctaRightLink?.url}
                          {...ctaRight}
                          align="left"
                          handleClick={handleClick}
                        >
                          {ctaRight?.title}
                        </Button>
                      </ButtonWrapper>
                    </Container>
                    <CTALeftLink to={ctaLeftLink?.url} />
                    <Desktop>
                      <Video
                        video={videoDesktop}
                        videoBackground={videoDesktopBackground}
                        playing={true}
                      />
                    </Desktop>
                    <Mobile>
                      <Video
                        video={videoMobile}
                        videoBackground={videoMobileBackground}
                        playing={true}
                      />
                    </Mobile>
                    <CTARightLink to={ctaRightLink?.url} />
                  </FullSection>
                </Wrapper>
              );
            case "Full Width Image":
              return (
                <FullSection>
                  <CTALeftLink to={ctaLeftLink?.url} />
                  <Container>
                    <DesktopTitle>{title}</DesktopTitle>
                    <ButtonWrapper>
                      <Button
                        as={Link}
                        to={ctaLeftLink?.url}
                        {...ctaLeft}
                        align="right"
                        handleClick={handleClick}
                      >
                        {ctaLeft?.title}
                      </Button>
                      <Button
                        as={Link}
                        to={ctaRightLink?.url}
                        {...ctaRight}
                        align="left"
                        handleClick={handleClick}
                      >
                        {ctaRight?.title}
                      </Button>
                    </ButtonWrapper>
                  </Container>
                  <Desktop>
                    <ImageWrapper>
                      <Image
                        ratio={
                          desktopAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                        }
                        alt={imageDesktop?.alt}
                        image={imageDesktop}
                        onClick={handleClick}
                        critical={true}
                      />
                    </ImageWrapper>
                  </Desktop>
                  <Mobile>
                    <ImageWrapper>
                      <Image
                        ratio={
                          mobileAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                        }
                        alt={imageMobile?.alt}
                        image={imageMobile}
                        onClick={handleClick}
                        critical={true}
                      />
                    </ImageWrapper>
                  </Mobile>
                  <CTARightLink to={ctaRightLink?.url} />
                </FullSection>
              );
            default:
              return (
                <Wrapper>
                  <Container>
                    <DesktopTitle>{title}</DesktopTitle>
                    <ButtonWrapper>
                      <Button
                        as={Link}
                        to={ctaLeftLink?.url}
                        {...ctaLeft}
                        align="right"
                        handleClick={handleClick}
                      >
                        {ctaLeft?.title}
                      </Button>
                      <Button
                        as={Link}
                        to={ctaRightLink?.url}
                        {...ctaRight}
                        align="left"
                        handleClick={handleClick}
                      >
                        {ctaRight?.title}
                      </Button>
                    </ButtonWrapper>
                  </Container>
                  <LeftSection>
                    <Mobile>
                      <ImageWrapper>
                        <Image
                          ratio={
                            mobileAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                          }
                          alt={imageLeft?.alt}
                          image={imageLeft}
                          imageLink={ctaLeftLink}
                          onClick={handleClick}
                          critical={true}
                        />
                      </ImageWrapper>
                    </Mobile>
                    <Desktop>
                      <ImageWrapper>
                        <Image
                          ratio={
                            desktopAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                          }
                          alt={imageLeftDesktop.alt}
                          image={imageLeftDesktop}
                          imageLink={ctaLeftLink}
                          onClick={handleClick}
                          critical={true}
                        />
                      </ImageWrapper>
                    </Desktop>
                  </LeftSection>
                  <RightSection>
                    <Mobile>
                      <ImageWrapper>
                        <Image
                          ratio={
                            mobileAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                          }
                          alt={imageRight?.alt}
                          image={imageRight}
                          imageLink={ctaRightLink}
                          onClick={handleClick}
                          critical={true}
                        />
                      </ImageWrapper>
                    </Mobile>
                    <Desktop>
                      <ImageWrapper>
                        <Image
                          ratio={
                            desktopAspectRatio?.aspectRatio as keyof ThemeAspectRatio
                          }
                          alt={imageRightDesktop?.alt}
                          image={imageRightDesktop}
                          imageLink={ctaRightLink}
                          onClick={handleClick}
                          critical={true}
                        />
                      </ImageWrapper>
                    </Desktop>
                  </RightSection>
                </Wrapper>
              );
          }
        })()}
      </BackgroundContainer>
    );
  },
);

export default HeroDuo;
